import { createFileRoute } from "@tanstack/react-router";
import { i18nLinks, i18nOgMeta, localeUrl } from "@/lib/seo";
import { m } from "@/paraglide/messages";
import { unscrambleQueryOptions } from "@/tools/word-unscrambler/hooks/use-unscramble";
import { getCurated } from "@/tools/word-unscrambler/lib/curated";
import { normalizeLetters } from "@/tools/word-unscrambler/lib/unscramble";
import { LettersPage } from "@/tools/word-unscrambler/pages/LettersPage";

export const Route = createFileRoute("/word-unscrambler/$letters")({
  ssr: true,
  loader: async ({ context, params }) => {
    // Prefetch server-side so the word results are in the SSR HTML.
    await context.queryClient.ensureQueryData(
      unscrambleQueryOptions(params.letters),
    );
  },
  head: ({ params }) => {
    const norm = normalizeLetters(params.letters).slice(0, 12);
    const upper = norm.toUpperCase();
    // Canonicalize variants (LISTEN, l-i-s-t-e-n) to the normalized path.
    const path = `/word-unscrambler/${norm}`;
    return {
      meta: [
        // Only the curated, sitemap-listed words are indexable. Every result
        // word links to its own letters page, so without this the catch-all
        // exposes an unbounded graph of near-identical template pages.
        ...(getCurated(norm)
          ? []
          : [{ name: "robots", content: "noindex, follow" }]),
        { title: m.wu_letters_meta_title({ letters: upper }) },
        {
          name: "description",
          content: m.wu_letters_meta_description({ letters: upper }),
        },
        {
          property: "og:title",
          content: m.wu_letters_meta_title({ letters: upper }),
        },
        {
          property: "og:description",
          content: m.wu_letters_meta_description({ letters: upper }),
        },
        ...i18nOgMeta(path),
      ],
      links: [...i18nLinks(path)],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: m.wu_breadcrumb_home(),
                item: localeUrl(`/`),
              },
              {
                "@type": "ListItem",
                position: 2,
                name: m.wu_tool_name(),
                item: localeUrl(`/word-unscrambler`),
              },
              {
                "@type": "ListItem",
                position: 3,
                name: upper,
                item: localeUrl(`${path}`),
              },
            ],
          }),
        },
      ],
    };
  },
  component: LettersRoute,
});

function LettersRoute() {
  const { letters } = Route.useParams();
  return <LettersPage letters={letters} />;
}
